import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useApi } from "@/lib/api";
import { useBrand } from "@/lib/brand";
import { PageHeader, Panel, StatusBadge, PageLoader, Btn, TextInput, EmptyState, SectionTitle, brandName } from "@/lib/ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Workflow, Plus, ArrowRight } from "lucide-react";

const EVENTS = ["ai.discovery", "approval.created", "upload.processed", "task.due", "integration.error"];
const ACTIONS = ["create_review", "notify_owner", "create_task", "send_email"];

function NewAutomation({ brand }) {
  const api = useApi();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", event: "ai.discovery", field: "confidence", op: "gte", value: "0.8", actions: ["create_review", "notify_owner"] });
  const create = useMutation({
    mutationFn: () => api.post("/automations", {
      name: form.name, brand_id: brand, when: { event: form.event },
      conditions: [{ field: form.field, op: form.op, value: form.value }],
      then: form.actions.map((a) => ({ action: a })), enabled: false,
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["automations"] }); setOpen(false); toast.success("Automation created (paused)"); },
    onError: (e) => toast.error(e.message),
  });
  const toggleAction = (a) => setForm((f) => ({ ...f, actions: f.actions.includes(a) ? f.actions.filter((x) => x !== a) : [...f.actions, a] }));

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Btn data-testid="new-automation-btn"><Plus className="h-4 w-4" /> New Workflow</Btn></DialogTrigger>
      <DialogContent className="bg-popover border-border">
        <DialogHeader><DialogTitle className="font-head">Workflow · WHEN / IF / THEN</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <TextInput label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="automation-name" />
          <div>
            <SectionTitle className="mb-1.5 text-ggg">WHEN</SectionTitle>
            <select value={form.event} onChange={(e) => setForm({ ...form, event: e.target.value })} className="w-full h-9 rounded-md border border-border bg-surface px-3 text-sm" data-testid="automation-event">
              {EVENTS.map((ev) => <option key={ev}>{ev}</option>)}
            </select>
          </div>
          <div>
            <SectionTitle className="mb-1.5 text-warn">IF</SectionTitle>
            <div className="grid grid-cols-3 gap-2">
              <TextInput value={form.field} onChange={(e) => setForm({ ...form, field: e.target.value })} />
              <select value={form.op} onChange={(e) => setForm({ ...form, op: e.target.value })} className="h-9 rounded-md border border-border bg-surface px-2 text-sm">
                {["gte", "lte", "eq", "gt", "lt"].map((o) => <option key={o}>{o}</option>)}
              </select>
              <TextInput value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} />
            </div>
          </div>
          <div>
            <SectionTitle className="mb-1.5 text-ok">THEN</SectionTitle>
            <div className="flex flex-wrap gap-2">
              {ACTIONS.map((a) => (
                <button key={a} onClick={() => toggleAction(a)} type="button"
                  className={`text-xs border rounded-full px-3 py-1 transition-colors ${form.actions.includes(a) ? "border-ok text-ok bg-ok/10" : "border-border text-muted-foreground"}`}>
                  {a}
                </button>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter><Btn onClick={() => create.mutate()} disabled={!form.name || create.isPending} data-testid="automation-submit">Create</Btn></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Automations() {
  const api = useApi();
  const qc = useQueryClient();
  const { brand } = useBrand();
  const { data: items = [], isLoading } = useQuery({ queryKey: ["automations", brand], queryFn: () => api.get(`/automations?brand_id=${brand}`) });
  const toggle = useMutation({
    mutationFn: ({ id, enabled }) => api.patch(`/automations/${id}`, { enabled }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["automations"] }); },
    onError: (e) => toast.error(e.message),
  });

  if (isLoading) return <PageLoader />;

  return (
    <div>
      <PageHeader kicker={`${brandName(brand)} · GGG-11`} title="Automation Engine"><NewAutomation brand={brand} /></PageHeader>
      {items.length === 0 ? (
        <EmptyState icon={Workflow} title="No workflows yet" hint="Build reusable WHEN / IF / THEN workflows. Example: WHEN AI discovers information → IF confidence is high → THEN create a review item and notify the Owner." />
      ) : (
        <div className="space-y-3">
          {items.map((a) => (
            <Panel key={a.id} className="p-4" data-testid={`automation-${a.id}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="font-head font-bold">{a.name}</div>
                <div className="flex items-center gap-3">
                  <StatusBadge tone={a.enabled ? "ok" : "muted"} dot={a.enabled}>{a.enabled ? "Active" : "Paused"}</StatusBadge>
                  <Switch checked={a.enabled} onCheckedChange={(v) => toggle.mutate({ id: a.id, enabled: v })} data-testid={`toggle-automation-${a.id}`} />
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 text-sm font-mono">
                <span className="text-ggg text-xs">WHEN</span>
                <span className="border border-border rounded px-2 py-0.5 text-xs">{a.when?.event}</span>
                <ArrowRight className="h-3 w-3 text-muted-foreground" />
                <span className="text-warn text-xs">IF</span>
                {(a.conditions || []).map((c, i) => <span key={i} className="border border-border rounded px-2 py-0.5 text-xs">{c.field} {c.op} {c.value}</span>)}
                <ArrowRight className="h-3 w-3 text-muted-foreground" />
                <span className="text-ok text-xs">THEN</span>
                {(a.then || []).map((t, i) => <span key={i} className="border border-ok/30 text-ok rounded px-2 py-0.5 text-xs">{t.action}</span>)}
              </div>
            </Panel>
          ))}
        </div>
      )}
    </div>
  );
}
